import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ScrollRestoration, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import MainContent from "./components/MainContent/MainContent";
import { ArrayDataProvider } from "./context/ArrayDataContext";
import { setTitle } from "./redux/actions/dashboardTitle";
import { setSelectedPlant } from "./redux/actions/userSlice";
import { useIsAuthenticated } from "@azure/msal-react";
import { Loader } from "./components/common/Loader";
import SideDrawer from "./components/SideDrawer/SideDrawer";
import {
  AllEnterpriseModule,
  IntegratedChartsModule,
  LicenseManager,
  ModuleRegistry,
} from "ag-grid-enterprise";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { componentsPath } from "./routes/PrivateRoutes";
import { AppSnackbarProvider } from "./components/SnakBar/AppSnackbarProvider";
import SupportFab from "./components/SupportTicket/SupportTicket";
import SupportTicketDialog from "./components/SupportTicket/SupportTicketForm";
import {
  setIsSelectionRemove,
  setSelectedDropdownValue,
  setSelectedRow,
} from "./redux/actions/supportTicket";
import { excludePath } from "./utils/constants";
import { GlobalToast } from "./components/common/GlobalToast";


LicenseManager.setLicenseKey(
  "Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-065772}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{The_Coca_Cola_Company}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Topline_Forecast}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Topline_Forecast}_need_to_be_licensed___{Topline_Forecast}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{21_September_2025}____[v3]_[0102]_MTc1ODQwOTIwMDAwMA==372521b73ce277d3fad471863dd69994",
);
ModuleRegistry.registerModules([
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule),
]);

const App: React.FC = () => {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const authStatus = useIsAuthenticated();
  const currentRole = useSelector((state: any) => state?.userRoles?.currentRole);
  const isAuthenticated =
    String(process.env.REACT_APP_BYPASS_AUTH).toLowerCase() === "true"
      ? true
      : authStatus;
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const tabDetails = useSelector(
    (state: any) => state?.dashboardDetails?.tabDetails,
  );
  const [open, setOpen] = React.useState(false);
  useLayoutEffect(() => {
    let animationFrameId: number | undefined;
    const measure = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        if (height > 0) {
          setHeaderHeight(height);
        } else {
          animationFrameId = window.requestAnimationFrame(measure);
        }
      } else {
        animationFrameId = window.requestAnimationFrame(measure);
      }
    };

    animationFrameId = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
    };
  }, [tabDetails]);

  const handleMenuItemClick = (path: string, label: string, item: any) => {
    // Always use SPA navigation to preserve ArrayDataProvider state
    navigate(path, { state: { label: item.label } });
    dispatch(setTitle(item));
  };

  useEffect(() => {
    const foundItem = componentsPath?.find(
      (item) => item.path === location.pathname,
    );
    if (foundItem) {
      dispatch(setTitle(foundItem));
    }
  }, [dispatch, location.pathname]);

  useEffect(() => {
    dispatch(setSelectedRow([]));
    dispatch(setSelectedDropdownValue(null));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    let plants: string[] = [];
    const rawSelectedPlant = sessionStorage.getItem("selectedPlant");
    if (rawSelectedPlant) {
      try {
        const parsed = JSON.parse(rawSelectedPlant);
        if (Array.isArray(parsed)) {
          plants = parsed.map((p) => String(p));
        }
      } catch {
        plants = [];
      }
    }

    if (plants.length === 0) {
      const params = new URLSearchParams(location.search);
      const rawPlant = params.get("plant");
      if (rawPlant) {
        plants = [rawPlant.split("-")[0]?.trim()];
      }
    }

    if (plants.length > 0) {
      dispatch(setSelectedPlant(plants));
    }
  }, [dispatch, location.pathname, location.search]);

  const isSupportFabShow = () => {
    return !excludePath.includes(location.pathname);
  };

  return isAuthenticated ? (
    <ArrayDataProvider>
      <AppSnackbarProvider>
        <GlobalToast />
        <div className="App">
          <ScrollRestoration />
          <Box
            sx={ {
              margin: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              overflowX: "hidden",
            } }
          >
            <Header headerRef={ headerRef } />

            <Box
              sx={ {
                display: "flex",
                paddingTop: `${headerHeight}px`,
                transition: "padding-top 0.3s ease",
                flexGrow: 1,
              } }
            >
              <MainContent
                contentHeight={ headerHeight }
                handleMenuItemClick={ handleMenuItemClick }
              />
            </Box>
            { isSupportFabShow() && (
              <SupportFab
                label="Support Ticket"
                bottom={ 80 }
                right={ 24 }
                onClick={ () => {
                  dispatch(setIsSelectionRemove(false));
                  setOpen(true);
                } }
              />
            ) }

            <SupportTicketDialog
              open={ open }
              setOpen={ setOpen }
              role={ currentRole || undefined }
            />

            <Footer />
          </Box>
        </div>
      </AppSnackbarProvider>
    </ArrayDataProvider>
  ) : (
    <div
      style={ { display: "flex", flexDirection: "column", alignItems: "center" } }
    >
      <Loader />
    </div>
  );
};

export default App;
